import { z } from 'zod';
import { isoDateSchema } from './day';
import { BULK_STATUS_LIMIT, mapSpotSchema } from './map';
import { reservationSourceSchema, reservationStatusSchema } from './reservation';

// A clock time on the venue's own wall, as the arrival field is typed. §10
export const clockTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'must be HH:MM');

const guestName = z.string().trim().min(1).max(120);

export const reservationSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  spotId: z.uuid(),
  spotNumber: z.number().int().positive(),
  zoneKey: z.string(),
  guestName: z.string(),
  guestPhone: z.string().nullable(),
  guestEmail: z.string().nullable(),
  startsOn: isoDateSchema,
  endsOn: isoDateSchema,
  holdUntil: z.string(),
  holdMinutes: z.number().int().nonnegative(),
  holdFeeCents: z.number().int().nonnegative(),
  dayPriceCents: z.number().int().nonnegative(),
  depositCents: z.number().int().nonnegative(),
  arrivalTime: clockTimeSchema.nullable(),
  notes: z.string().nullable(),
  source: reservationSourceSchema,
  status: reservationStatusSchema,
});

export type Reservation = z.infer<typeof reservationSchema>;

// One guest, one range, one hold — one reservation per spot picked. §10
export const reservationRequestSchema = z
  .object({
    spotIds: z.array(z.uuid()).min(1).max(BULK_STATUS_LIMIT),
    guestName,
    guestPhone: z.string().trim().max(40).nullable().default(null),
    guestEmail: z.string().trim().max(160).nullable().default(null),
    startsOn: isoDateSchema,
    endsOn: isoDateSchema,
    // null holds nothing: the guest is already here and the spot is theirs. §10
    holdMinutes: z
      .number()
      .int()
      .min(0)
      .max(24 * 60)
      .nullable(),
    arrivalTime: clockTimeSchema.nullable().default(null),
    depositCents: z.number().int().nonnegative().max(1_000_000).default(0),
    notes: z.string().trim().max(500).nullable().default(null),
  })
  .refine((body) => body.endsOn >= body.startsOn, {
    path: ['endsOn'],
    message: 'must not end before it starts',
  });

export type ReservationRequest = z.infer<typeof reservationRequestSchema>;

export const holdExtensionSchema = z.object({
  holdMinutes: z
    .number()
    .int()
    .min(0)
    .max(24 * 60),
});

export type HoldExtension = z.infer<typeof holdExtensionSchema>;

// Every write answers with both halves: the bookings, and the tiles they moved.
export const reservationsResponseSchema = z.object({
  reservations: z.array(reservationSchema),
  spots: z.array(mapSpotSchema),
});

export type ReservationsResponse = z.infer<typeof reservationsResponseSchema>;

export const reservationRangeSchema = z
  .object({ from: isoDateSchema, to: isoDateSchema })
  .refine((query) => query.to >= query.from, {
    path: ['to'],
    message: 'must not end before it starts',
  });

export type ReservationRange = z.infer<typeof reservationRangeSchema>;

// What the activity screen restores, and what an Undo in a toast repeats. §10
export const ACTIVITY_KINDS = ['reserved', 'checkedIn', 'cancelled', 'expired'] as const;

export const activityKindSchema = z.enum(ACTIVITY_KINDS);

export type ActivityKind = z.infer<typeof activityKindSchema>;

// conflict names the booking in the way, so Restore can say why it is closed. §10
export const activityEntrySchema = z.object({
  id: z.uuid(),
  kind: activityKindSchema,
  at: z.string(),
  actorName: z.string().nullable(),
  reservationId: z.uuid(),
  code: z.string(),
  guestName: z.string(),
  spotNumber: z.number().int().positive(),
  restorable: z.boolean(),
  // Money already back to the guest: §13 will not let §10 hand the spot over too.
  refunded: z.boolean(),
  conflict: z.string().nullable(),
});

export type ActivityEntry = z.infer<typeof activityEntrySchema>;

// The venue's own clock travels with the feed: the times are read on it. §3
export const activityResponseSchema = z.object({
  entries: z.array(activityEntrySchema),
  timezone: z.string(),
});

export type ActivityResponse = z.infer<typeof activityResponseSchema>;

export const ACTIVITY_LIMIT = 40;
